import * as React from 'react';
import { QuestionnaireFieldListDto } from '@common/dto/questionnaire/field/list-dto';
import isNil from 'lodash/isNil';
import { QuestionnaireFieldOptionListLocalDto } from '@modules/fhq-design-tool/dto/field-option';
import { generateId } from '@common/utils/common';

export type DropdownOptionsProps = {
  disabled?: boolean;
  className?: string;
  data: Array<QuestionnaireFieldOptionListLocalDto>;
  possibleOptionsValue: QuestionnaireFieldListDto['possibleDropdownOptions']; 
  onChange: (data: Array<QuestionnaireFieldOptionListLocalDto>) => void;
};

export const useDropdownOptionsController = (props: DropdownOptionsProps) => {
  const { data, onChange, possibleOptionsValue } = props;
  const [selectOptionId, setSelectedOptionId] = React.useState<string | undefined>(undefined);

  const handleDisplayChange = React.useCallback((val: boolean, e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(data.map((x) => x.id === e.currentTarget.dataset.id ? ({ ...x, displayInFHQ: val }) : x));
  }, [data, onChange]);

  const handleDropdownValueChange = React.useCallback((option: QuestionnaireFieldOptionListLocalDto, val: string) => {
    onChange(data.map((x) => x.id === option.id ? ({ ...x, dropdownValue: val }) : x));
  }, [data, onChange]);

  const handleRowClick = React.useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    setSelectedOptionId((e.currentTarget as unknown as { dataset: { id: string } }).dataset.id);
  }, []);

  const handleDelete = React.useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    onChange(data.filter((x) => x.id !== e.currentTarget.dataset.id));
  }, [data, onChange]);

  const handleDisplayValueChange = React.useCallback((option: QuestionnaireFieldOptionListLocalDto, val: string) => {
    onChange(data.map((x) => x.id === option.id ? ({ ...x, displayValue: val }) : x));
  }, [data, onChange]);

  const getOptionCreateDto = React.useCallback((): QuestionnaireFieldOptionListLocalDto | undefined => {
    const defaultDropdownValue = possibleOptionsValue.find((value) => data.every((option) => option.dropdownValue !== value)) ?? possibleOptionsValue[0];

    if (isNil(defaultDropdownValue)) {
      return;
    }

    return {
      id: generateId(),
      dropdownValue: defaultDropdownValue,
      displayValue: `${defaultDropdownValue} - new`,
      displayInFHQ: true,
      canBeDeleted: true,
    };
  }, [possibleOptionsValue, data]);

  const handleInsertOption = React.useCallback(() => {
    const optionToAdd = getOptionCreateDto();

    if (!optionToAdd) {
      return;
    }

    const selectedOption = data.find((x) => x.id === selectOptionId);
    const index = data.findIndex((x) => selectedOption?.canBeDeleted 
      ? x.id === selectOptionId
      : x.canBeDeleted);

    if (index >=0) {
      const newData = [...data];
      newData.splice(index, 0, optionToAdd);
      onChange(newData);
    } else {
      onChange([...data, optionToAdd]);
    }
  }, [data, onChange, selectOptionId, getOptionCreateDto]);
  
  const handleAddOption = React.useCallback(() => {
    const optionToAdd = getOptionCreateDto();

    if (optionToAdd) {
      onChange([...data, optionToAdd]);
    }
  }, [data, onChange]);

  return {
    handleRowClick,
    selectOptionId,
    handleDropdownValueChange,
    handleDisplayChange,
    handleInsertOption,
    handleAddOption,
    handleDelete,
    handleDisplayValueChange,
  };
};